import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form } from '@unform/web';

import { Painel } from '../../../../components/Painel';
import { Icon } from '../../../../components/Icon';
import { Select } from '../../../../components/Select';
import { Pagination } from '../../../../components/Pagination';
import { SearchField } from '../../../../components/SearchField';
import {
  EmptyState,
  Table,
  TableWrapper,
  Tbody,
  Td,
  Th,
  Thead,
} from '../../../../components/Table';

import {
  Avatar,
  Container,
  FiltersRow,
  Header,
  LevelBadge,
  MemberCell,
  MemberName,
  MemberUsername,
  PageSubtitle,
  PageTitle,
  RowLink,
  SelectWrapper,
  StatusBadge,
} from './styles';

import { api } from '../../../../services/api';
import { colors } from '../../../../styles/tokens';
import {
  labelLevelAccess,
  resolveAccessLevel,
} from '../../../../utils/labelLeverAccess';
import noAvatar from '../../../../assets/no-avatar.svg';

// "Aluno(a)" não tem filtro dedicado -- o backend só filtra por
// permissão presente (`permission_code`), não por "nenhuma permissão".
// Mesma lista de users/pages/users/index.tsx (Diretório).
const accessLevel = [
  { value: '', label: 'Todos(as)' },
  { value: 'view_gym_dashboard', label: 'Funcionário(a)' },
  { value: 'manage_gym_users', label: 'Administrador(a)' },
];

interface IStaffProps {
  id: string;
  fullname: string;
  username: string;
  email?: string;
  permissions: string[];
  avatar_url: string | undefined;
  is_active?: boolean;
}

const PAGE_SIZE = 10;

// Lista TODOS os usuários da academia por padrão -- inclusive quem
// hoje só tem `student`, já que o ponto desta tela é justamente poder
// dar a primeira permissão de staff pra alguém. `select` (mesmas
// opções do Diretório) filtra por nível quando escolhido; vazio =
// sem filtro de permissão nenhum.
const StaffPermissions: React.FC = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [staff, setStaff] = useState<IStaffProps[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [select, setSelect] = useState('');
  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchText), 400);

    return () => clearTimeout(timer);
  }, [searchText]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, select]);

  const fetchStaff = useCallback(() => {
    setLoading(true);
    api
      .get('users/gym', {
        params: {
          page,
          limit: PAGE_SIZE,
          search: debouncedSearch || undefined,
          permission_code: select || undefined,
        },
      })
      .then(response => {
        setStaff(response?.data.data);
        setTotal(response?.data.total);
        setTotalPages(response?.data.totalPages);
      })
      .catch(err => console.log(err?.response?.status))
      .finally(() => setLoading(false));
  }, [page, debouncedSearch, select]);

  useEffect(() => {
    fetchStaff();
  }, [fetchStaff]);

  const handleChangeSelectForm = useCallback((e: any) => {
    setSelect(e.value);
  }, []);

  return (
    <Painel loading={loading}>
      <Container>
        <Header>
          <div>
            <PageTitle>Permissões de Funcionários</PageTitle>
            <PageSubtitle>
              Gerenciando o acesso de {total} usuário(s) da academia ao
              painel
            </PageSubtitle>
          </div>
        </Header>

        <FiltersRow>
          <SearchField
            placeholder="Buscar por nome ou usuário..."
            value={searchText}
            onChange={setSearchText}
          />
          <Form onSubmit={() => undefined}>
            <SelectWrapper>
              <Select
                name="select"
                options={accessLevel}
                placeholder="Nível de acesso"
                onChange={handleChangeSelectForm}
                blurInputOnSelect
              />
            </SelectWrapper>
          </Form>
        </FiltersRow>

        <TableWrapper>
          <Table>
            <Thead>
              <tr>
                <Th>Usuário</Th>
                <Th>Nível de acesso</Th>
                <Th>Status</Th>
                <Th>Ações</Th>
              </tr>
            </Thead>
            <Tbody>
              {staff.map(member => (
                <tr key={member.id}>
                  <Td>
                    <MemberCell>
                      <Avatar
                        src={member.avatar_url || noAvatar}
                        alt={member.fullname}
                      />
                      <div>
                        <MemberName>{member.fullname}</MemberName>
                        <MemberUsername>
                          {member.email || `@${member.username}`}
                        </MemberUsername>
                      </div>
                    </MemberCell>
                  </Td>
                  <Td>
                    <LevelBadge level={resolveAccessLevel(member.permissions)}>
                      {labelLevelAccess(member.permissions)}
                    </LevelBadge>
                  </Td>
                  <Td>
                    <StatusBadge
                      status={member.is_active === false ? 'inactive' : 'active'}
                    >
                      {member.is_active === false ? 'Inativo' : 'Ativo'}
                    </StatusBadge>
                  </Td>
                  <Td>
                    <RowLink
                      type="button"
                      title="Editar permissões"
                      onClick={() =>
                        navigate(`/users/${member.id}/permissions`)
                      }
                    >
                      <Icon
                        name="settings"
                        size={20}
                        color={colors.onSurfaceVariant}
                      />
                    </RowLink>
                  </Td>
                </tr>
              ))}
            </Tbody>
          </Table>

          {!staff.length && (
            <EmptyState>Nenhum usuário encontrado.</EmptyState>
          )}
        </TableWrapper>

        <Pagination
          page={page}
          totalPages={totalPages}
          total={total}
          pageSize={PAGE_SIZE}
          itemLabel="usuários"
          onPageChange={setPage}
        />
      </Container>
    </Painel>
  );
};

export { StaffPermissions };
